import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, Button, Tab, Tabs, TextField, Box, Typography, Grid, IconButton, DialogActions } from "@mui/material";
import { useParams } from "react-router-dom";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import moment from "moment";
import TableComponent from "components/grid";
import CustomPagination from "components/CustomPagination";
import { RxCross2 } from "react-icons/rx";
import StatusBadge from "components/badge/StatusBadge";
import { useLazyGetDealsListQuery } from "store/slices/apis/dealApiSlice";



const DealListPopUp = (props) => {
    const { header, open, handleClose, filter } = props;
    const { projectId } = useParams();
    const [loading, setLoading] = useState(false);
    const [sortOrder, setSortOrder] = React.useState('desc');
    const [sortBy, setSortBy] = React.useState('createdDate');
    const [pageNumber, setPageNumber] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(5);
    const dispatch = useDispatch();
    const [subTabValue, setSubTabValue] = useState(0)


    const [filters, setFilters] = useState({
        ...filter,
        ProjectId: projectId,
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,


    });

    const [trigger, { data: dealsList, isFetching }] = useLazyGetDealsListQuery();

    useEffect(() => {
        trigger(filters);
    }, []);




    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';

        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const numericRenderer = (field) => (row) => {
        const value = row?.[field];

        if (!value && value !== 0) return "N/A";

        return Number(value).toLocaleString("en-IN");
    };

    const col = [
        {
            name: "ID",
            field: "customId",
            width: "100px",
            sticky: true,
        },
        {
            name: "Lead Name",
            field: "leadName",
            width: "200px",
            sticky: true,
            renderer: (row) => row?.leadName ? row?.leadName : "N/A",
        },
        {
            name: "Deal Name",
            field: "name",
            width: "200px",
            sticky: true,
            renderer: (row) => row?.name ? row.name : "N/A",
        },
        {
            name: "Asset Type",
            field: "assetType",
            width: "250px",
            renderer: (row) => row?.assetType ? row.assetType : "N/A",
        },
        {
            name: "Area In Sqft",
            field: "area",
            width: "200px",
            renderer: numericRenderer("area"),
        },
        {
            name: "LOI Signed Date",
            field: "loiSignedDate",
            renderer: (row) => (
                row?.loiSignedDate ? moment.utc(row.loiSignedDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Model Rental Rate",
            field: "modelRentalRate",
            renderer: numericRenderer("modelRentalRate"),
        },
        {
            name: "Deal Rental Base",
            field: "dealRentalBase",
            renderer: numericRenderer("dealRentalBase"),
        },
        {
            name: "Deal Rental CAM",
            field: "dealRentalCam",
            renderer: numericRenderer("dealRentalCam"),
        },
        {
            name: "Deal Rental TI",
            field: "dealRentalTi",
            renderer: numericRenderer("dealRentalTi"),
        },
        {
            name: "Rental Comments",
            field: "rentalComments",
            width: "300px",
            renderer: (row) => row?.rentalComments ? row?.rentalComments : "N/A",
        },
        {
            name: "LCD",
            field: "lcd",
            renderer: (row) => (
                row?.lcd ? moment.utc(row.lcd).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Rent Free Months",
            field: "rentFreeMonths",
            renderer: (row) => row?.rentFreeMonths ?? "N/A",
        },
        {
            name: "RCD",
            field: "rcd",
            renderer: (row) => (
                row?.rcd ? moment.utc(row.rcd).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Escalation",
            field: "escalation",
            renderer: (row) => row?.escalation ? row.escalation : "N/A",
        },
        {
            name: "Notice Period (Days)",
            field: "noticePeriodDays",
            renderer: (row) => row?.noticePeriodDays ?? "N/A",
        },
        {
            name: "Security Deposite Amount",
            field: "securityDeposit",
            renderer: (row) => row?.securityDeposit ? row?.securityDeposit : "N/A",
        },
        {
            name: "Utilities Amount",
            field: "utilities",
            renderer: (row) => row?.utilities ? row?.utilities : "N/A",
        },
        {
            name: "Stamp Duty (Lesse)",
            field: "stampDutyLessee",
            renderer: (row) => row?.stampDutyLessee ? row?.stampDutyLessee : "N/A",
        },
        {
            name: "Stamp Duty (Lessor)",
            field: "stampDutyLessor",
            renderer: numericRenderer("stampDutyLessor"),
        },
        {
            name: "Handover Date",
            field: "handoverDate",
            renderer: (row) => (
                row?.handoverDate ? moment.utc(row.handoverDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Lease To Handover (Days)",
            field: "leaseToHandoverDays",
            renderer: (row) => row?.leaseToHandoverDays ?? "N/A",
        },
        {
            name: "Post Handover Esclations",
            field: "postHandoverEscalations",
            renderer: (row) => row?.postHandoverEscalations ? row?.postHandoverEscalations : "N/A",
        },
    ];




    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <Dialog open={open}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >

            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
            }}>
                <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                    {header}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose()}
                    sx={(theme) => ({
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    {/* <IoMdClose style={{ fontSize: "20px" }} /> */}
                    <RxCross2 style={{ fontSize: "30px", color: "#140F36" }} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container>
                    <Grid size={{ md: 12 }}>
                        <TableComponent
                            columns={col}
                            data={dealsList?.data || []}
                            createSortHandler={handleRequestSort}
                            loading={isFetching}
                            order={sortOrder}
                            orderBy={sortBy}
                        />
                    </Grid>
                    <Grid size={{ md: 12 }}>
                        <CustomPagination
                            totalCount={dealsList?.totalRecords || 0}
                            pageSize={pageSize}
                            page={pageNumber}
                            onPageChange={handlePageChange}
                            setCurrentPage={setPageNumber}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default DealListPopUp;
